// @givmo/board — reading the board's self-assessment (CBG-YR4-ASSESS-WEB; the backend's
// `agent_docs/contracts/board-self-assessment.md`).
//
// ══ WHAT THESE READS ARE, AND WHAT THEY ARE NOT ═════════════════════════════════════════════════
//
// An assessment is the backend's WORKING STATE — who was asked, who has answered and when, by which
// road, what was sent and when — and its close writes ONE record, the results: how many chose each
// answer, statement by statement in the instrument's own order, and the comments without names. Every
// read here is either those facts or that record's own payload, and the screens show them as they
// come. Nothing is computed from them that the backend did not say: no average, no score, no rank, no
// percentage, no order by any count (plan §3.2, I6). The one figure a screen derives is which of the
// five counts are not zero, the record body's own rule (C11).
//
// ⚠ NO READ HERE CARRIES ANYONE'S ANSWERS BUT THE PERSON'S OWN (D-CBG-28, I4). The officer's reads
// say who answered, never what; the person's own read (D2) carries `my_answers` by the seat's custody
// chain; the results carry counts and unnamed comments, and nothing at all under three answer sets.
//
// Tolerant, as every Board decoder is: an answer missing a field, a list that is not an array, or a
// value this build doesn't know never throws. A malformed row is dropped; an unknown word is shown
// humanized rather than hidden.

import type {
  BoardSelfAssessmentAnswerOptionOut,
  BoardSelfAssessmentDirectorSummaryOut,
  BoardSelfAssessmentGroupOut,
  BoardSelfAssessmentInstrumentOut,
  BoardSelfAssessmentInstrumentSummaryOut,
  BoardSelfAssessmentOpenQuestionOut,
  BoardSelfAssessmentOut,
  BoardSelfAssessmentPersonOut,
  BoardSelfAssessmentPrioritiesOut,
  BoardSelfAssessmentQuestionsForMeOut,
  BoardSelfAssessmentQuestionsOut,
  BoardSelfAssessmentResultGroupOut,
  BoardSelfAssessmentResultOpenQuestionOut,
  BoardSelfAssessmentResultStatementOut,
  BoardSelfAssessmentResultsOut,
  BoardSelfAssessmentStatementOut,
  BoardSelfAssessmentSummaryOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { closeRecordDay, displayDate, displayLongDate, humanize } from '../../lib/display';
import { meetingDayOf } from '../in-meeting/in-meeting-decode';
import { capacityOf, isPastDue, mailDate, shortDay } from '../conflicts/coi-decode';
import { assessmentWords as w } from './assessment-words';

/**
 * The ask's or a reminder's lines as the backend's templates render them for this person (approved,
 * CBG-YR4-COPY1): the greeting, the ask with its date (after the due date, a reminder's own line),
 * the privacy line, and the joining line for a seat nobody has joined — each in its approved form
 * for an organization with no name. The date in the mail's own form ("February 26, 2027").
 */
export function assessmentEmailLines(
  act: 'request' | 'reminder',
  {
    name,
    organization,
    label,
    dueOn,
    joined,
  }: { name: string; organization: string | null; label: string; dueOn: string; joined: boolean },
): string[] {
  const due = mailDate(dueOn);
  const when =
    act === 'request'
      ? w.emailPleaseSendBy(due)
      : isPastDue(dueOn)
        ? w.emailWasDue(due)
        : w.emailYoursDue(due);
  return [
    w.emailHello(name),
    `${w.emailAsk(organization ?? w.emailYourOrganization, label)} ${when}`,
    w.emailPrivacy,
    ...(joined
      ? []
      : [organization === null ? w.emailNotJoinedUnnamed : w.emailNotJoined(organization)]),
  ];
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function arrayOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value.filter(isObject) as T[]) : [];
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.length > 0)
    : [];
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

// ── The board's assessments, and one assessment ──────────────────────────────────────────────────

/** Every assessment of this organization, newest first (the backend's order, by when it opened). */
export function extractAssessments(body: unknown): BoardSelfAssessmentSummaryOut[] {
  return extractList<BoardSelfAssessmentSummaryOut>(body, 'data').filter(
    (row) =>
      isObject(row) &&
      typeof row.id === 'string' &&
      typeof row.label === 'string' &&
      typeof row.status === 'string',
  );
}

/** The organization's open one, if any: one open per organization (the backend's fence). */
export function openAssessmentOf(
  assessments: readonly BoardSelfAssessmentSummaryOut[],
): BoardSelfAssessmentSummaryOut | null {
  return assessments.find((row) => row.status === 'open') ?? null;
}

/** The ones before the open one, or every one while none is open: closed and withdrawn alike. */
export function earlierAssessments(
  assessments: readonly BoardSelfAssessmentSummaryOut[],
): BoardSelfAssessmentSummaryOut[] {
  return assessments.filter((row) => row.status !== 'open');
}

/**
 * The name the next one would carry: this calendar year, or the next one no assessment that wasn't
 * withdrawn holds (a name is unique among those not withdrawn, so "2027" can be used again after a
 * mistaken one is withdrawn).
 */
export function nextLabel(
  assessments: readonly BoardSelfAssessmentSummaryOut[],
  nowMs: number = Date.now(),
): string {
  const taken = new Set(
    assessments.filter((row) => row.status !== 'cancelled').map((row) => row.label),
  );
  let year = new Date(nowMs).getFullYear();
  while (taken.has(String(year))) year += 1;
  return String(year);
}

/**
 * An earlier one as a line of the list: its results' day and count, or the day it was withdrawn. The
 * results' day is the one their record is dated by (`closeRecordDay`), the day the results and the
 * record say; a withdrawal writes no record, so its day is the reader's.
 */
export function earlierLine(row: BoardSelfAssessmentSummaryOut): string {
  if (row.status === 'cancelled')
    return w.earlierWithdrawn(row.label, displayDate(row.cancelled_at));
  return w.earlierRow(
    row.label,
    displayDate(closeRecordDay(row.closed_at)),
    count(row.answered_count),
    count(row.asked_count),
  );
}

export function decodeAssessment(body: unknown): BoardSelfAssessmentOut | null {
  if (!isObject(body) || typeof body.id !== 'string' || typeof body.label !== 'string') return null;
  const assessment = body as unknown as BoardSelfAssessmentOut;
  return {
    ...assessment,
    people: arrayOf<BoardSelfAssessmentPersonOut>(body.people).filter(
      (row) => typeof row.board_member_id === 'string',
    ),
    priorities: decodePriorities(body.priorities),
  };
}

export type StatusTone = 'plain' | 'act' | 'quiet';

/**
 * One person's standing, as plain words (Ramp's People table): "Answered" or "Not yet" — who has
 * answered, never what (D-CBG-28) — with the facts an officer acts on as quiet notes. Colour only
 * where the officer has something to do: someone the self-assessment hasn't been sent to.
 *
 * ⚠ A SEAT WITH NO ROW IS ONE OF TWO THINGS (`not_asked`). Added to the roster after it started
 * (`joined_after_open_on`), it waits on the officer's Send, as a year's joiner does: "Not sent yet",
 * the act colour (the annual disclosures' table). Unticked at the start, it was left out on purpose:
 * "Not asked", quiet. Its row still offers Send, by name (contract C20), but nothing asks the officer
 * to act on a choice they made.
 */
export function personStatus(person: BoardSelfAssessmentPersonOut): {
  word: string;
  tone: StatusTone;
  notes: string[];
} {
  const notes: string[] = [];
  if (person.joined_after_open_on)
    notes.push(w.joinedBoardOn(shortDay(person.joined_after_open_on)));
  switch (person.status) {
    case 'answered': {
      const paper = person.road === 'paper' || Boolean(person.handed_in_on_paper_at);
      if (paper) notes.push(w.onPaper);
      return { word: w.statusAnswered, tone: 'plain', notes };
    }
    case 'not_asked': {
      const joinerToSend = Boolean(person.can_send && person.joined_after_open_on);
      return {
        word: joinerToSend ? w.notSentYet : w.statusNotAsked,
        tone: joinerToSend ? 'act' : 'quiet',
        notes,
      };
    }
    case 'left':
      return {
        word: w.statusLeft(person.left_on ? shortDay(person.left_on) : null),
        tone: 'quiet',
        notes: [],
      };
    default: {
      if (!person.has_email) notes.push(w.noEmailOnRoster);
      else if (!person.joined_board) notes.push(w.hasntJoined);
      if (person.last_reminded_at) notes.push(w.remindedOn(shortDay(person.last_reminded_at)));
      if (!person.first_sent_at && person.is_recipient && person.can_send) {
        return { word: w.notSentYet, tone: 'act', notes };
      }
      return { word: w.statusNotYet, tone: 'quiet', notes };
    }
  }
}

/**
 * The people a Send to everyone reaches now, as the backend counts them (`sendable_count`, its
 * `sent_to_everyone`): someone asked who was never sent it, and a seat added to the roster since it
 * started. Never a seat the officer unticked at the start, which is sent only by name, from its own
 * row (contract C20), so the bulk send never mails someone the officer chose to leave out.
 */
export function sendablePeople(
  people: readonly BoardSelfAssessmentPersonOut[],
): BoardSelfAssessmentPersonOut[] {
  return people.filter(
    (person) =>
      person.can_send === true && (person.is_recipient || Boolean(person.joined_after_open_on)),
  );
}

/** The people a reminder can go to now (the backend's `can_remind`: at most once a day each). */
export function remindablePeople(
  people: readonly BoardSelfAssessmentPersonOut[],
): BoardSelfAssessmentPersonOut[] {
  return people.filter((person) => person.can_remind === true);
}

/**
 * How many marked paper hand-ins still wait for their sheet: marks before sheets (the backend's §4.2),
 * so a sheet can be recorded only while this is above zero.
 */
export function sheetsWaiting(assessment: BoardSelfAssessmentOut): number {
  const marks = assessment.people.filter((person) => Boolean(person.handed_in_on_paper_at)).length;
  return Math.max(0, marks - count(assessment.paper_sheet_count));
}

/** Whether a Withdraw is offered: the backend withdraws only before any answer set or mark. */
export function canWithdraw(assessment: BoardSelfAssessmentOut): boolean {
  return (
    assessment.status === 'open' &&
    count(assessment.answered_count) === 0 &&
    !assessment.people.some((person) => Boolean(person.handed_in_on_paper_at))
  );
}

/** A person's capacity line, as the roster's says it ("Director · Chair"). */
export function personCapacity(person: BoardSelfAssessmentPersonOut): string {
  return capacityOf({ is_director: person.is_director ?? null, title_labels: person.title_labels });
}

// ── The instrument, and the questions an assessment asks ─────────────────────────────────────────

export function extractInstruments(body: unknown): BoardSelfAssessmentInstrumentSummaryOut[] {
  return extractList<BoardSelfAssessmentInstrumentSummaryOut>(body, 'data').filter(
    (row) =>
      isObject(row) && typeof row.template_id === 'string' && typeof row.version === 'number',
  );
}

function decodeGroups(value: unknown): BoardSelfAssessmentGroupOut[] {
  return arrayOf<BoardSelfAssessmentGroupOut>(value)
    .filter((group) => typeof group.group_id === 'string')
    .map((group) => ({
      ...group,
      title: typeof group.title === 'string' ? group.title : '',
      statements: arrayOf<BoardSelfAssessmentStatementOut>(group.statements).filter(
        (statement) =>
          typeof statement.statement_id === 'string' && typeof statement.text === 'string',
      ),
    }))
    .filter((group) => group.statements.length > 0);
}

function decodeOpenQuestions(value: unknown): BoardSelfAssessmentOpenQuestionOut[] {
  return arrayOf<BoardSelfAssessmentOpenQuestionOut>(value).filter(
    (question) => typeof question.question_id === 'string' && typeof question.text === 'string',
  );
}

function decodeAnswerOptions(value: unknown): BoardSelfAssessmentAnswerOptionOut[] {
  return arrayOf<BoardSelfAssessmentAnswerOptionOut>(value).filter(
    (option) => typeof option.answer === 'string' && typeof option.label === 'string',
  );
}

export function decodeInstrument(body: unknown): BoardSelfAssessmentInstrumentOut | null {
  if (!isObject(body) || typeof body.template_id !== 'string') return null;
  return {
    ...(body as unknown as BoardSelfAssessmentInstrumentOut),
    groups: decodeGroups(body.groups),
    open_questions: decodeOpenQuestions(body.open_questions),
    answer_options: decodeAnswerOptions(body.answer_options),
  };
}

/** The questions an assessment asks, as frozen at its opening: what a paper sheet prints and enters. */
export function decodeQuestions(body: unknown): BoardSelfAssessmentQuestionsOut | null {
  if (!isObject(body) || typeof body.assessment_id !== 'string') return null;
  return {
    ...(body as unknown as BoardSelfAssessmentQuestionsOut),
    groups: decodeGroups(body.groups),
    open_questions: decodeOpenQuestions(body.open_questions),
    answer_options: decodeAnswerOptions(body.answer_options),
  };
}

/** The person's own questions and, while it is open, their own answers (D2). */
export function decodeQuestionsForMe(body: unknown): BoardSelfAssessmentQuestionsForMeOut | null {
  if (!isObject(body) || !isObject(body.assessment)) return null;
  const assessment = body.assessment as unknown as BoardSelfAssessmentDirectorSummaryOut;
  if (typeof assessment.assessment_id !== 'string') return null;
  const mine = isObject(body.my_answers) ? body.my_answers : null;
  return {
    ...(body as unknown as BoardSelfAssessmentQuestionsForMeOut),
    assessment,
    groups: decodeGroups(body.groups),
    open_questions: decodeOpenQuestions(body.open_questions),
    answer_options: decodeAnswerOptions(body.answer_options),
    my_answers:
      mine === null
        ? null
        : {
            ...mine,
            answers: arrayOf(mine.answers),
            comments: arrayOf(mine.comments),
            open_answers: arrayOf(mine.open_answers),
          },
  };
}

/** The seat's own assessments (D1), newest first. */
export function extractOwnAssessments(body: unknown): BoardSelfAssessmentDirectorSummaryOut[] {
  return extractList<BoardSelfAssessmentDirectorSummaryOut>(body, 'data').filter(
    (row) =>
      isObject(row) && typeof row.assessment_id === 'string' && typeof row.label === 'string',
  );
}

// ── The results (s3) ─────────────────────────────────────────────────────────────────────────────

export function decodeResults(body: unknown): BoardSelfAssessmentResultsOut | null {
  if (!isObject(body) || typeof body.assessment_id !== 'string' || !isObject(body.record)) {
    return null;
  }
  return {
    ...(body as unknown as BoardSelfAssessmentResultsOut),
    answer_options: decodeAnswerOptions(body.answer_options),
    groups: arrayOf<BoardSelfAssessmentResultGroupOut>(body.groups)
      .filter((group) => typeof group.group_id === 'string')
      .map((group) => ({
        ...group,
        title: typeof group.title === 'string' ? group.title : '',
        comments: strings(group.comments),
        statements: arrayOf<BoardSelfAssessmentResultStatementOut>(group.statements)
          .filter(
            (statement) =>
              typeof statement.statement_id === 'string' && typeof statement.text === 'string',
          )
          .map((statement) => ({
            ...statement,
            counts: arrayOf<{ answer: string; count: number }>(statement.counts).filter(
              (row) => typeof row.answer === 'string',
            ),
          })),
      })),
    open_questions: arrayOf<BoardSelfAssessmentResultOpenQuestionOut>(body.open_questions)
      .filter((question) => typeof question.question_id === 'string')
      .map((question) => ({ ...question, answers: strings(question.answers) })),
    priorities: decodePriorities(body.priorities),
  };
}

/**
 * One statement's counts as the record body prints them (C11): the answers in their own order, the
 * zeros left out, each with its label. Never sorted by a count. An answer this build doesn't know is
 * kept, humanized, in the place the wire gives it.
 */
export function countsShown(
  statement: Pick<BoardSelfAssessmentResultStatementOut, 'counts'>,
  options: readonly BoardSelfAssessmentAnswerOptionOut[],
): { answer: string; label: string; count: number }[] {
  const labels = new Map(options.map((option) => [option.answer, option.label]));
  return statement.counts
    .filter((row) => count(row.count) > 0)
    .map((row) => ({
      answer: row.answer,
      label: labels.get(row.answer) ?? humanize(row.answer),
      count: count(row.count),
    }));
}

// ── The board's priorities ───────────────────────────────────────────────────────────────────────

export function decodePriorities(value: unknown): BoardSelfAssessmentPrioritiesOut | null {
  if (!isObject(value) || typeof value.text !== 'string') return null;
  if (value.kind !== 'motion' && value.kind !== 'consent') return null;
  return {
    ...(value as unknown as BoardSelfAssessmentPrioritiesOut),
    statements: arrayOf<{ statement_id: string; text: string }>(value.statements).filter(
      (statement) => typeof statement.text === 'string',
    ),
  };
}

/**
 * Where the board chose its priorities, as s3 says it: "Chosen by the board at the {meeting}, {day}."
 * for a motion (the day in the MEETING's zone), or the day a written consent was signed by every
 * director. The read's own facts only; a fact it lacks drops from the sentence, never guessed.
 *
 * ⚠ A consent stays a consent. The read serves no `executed_on` only once the consent itself is gone
 * (its filed record removed after the retention floor, which takes the consent with it), and then it
 * still reads as a written consent, never as a motion.
 */
export function prioritiesSource(priorities: BoardSelfAssessmentPrioritiesOut): string {
  if (priorities.kind === 'consent') {
    const day = displayLongDate(priorities.executed_on ?? null);
    return day === null ? w.prioritiesByAConsent : w.prioritiesByConsent(day);
  }
  const day = meetingDayOf({
    starts_at: priorities.meeting_starts_at ?? null,
    time_zone: priorities.meeting_time_zone ?? null,
  });
  const title = priorities.meeting_title?.trim() ?? '';
  const written = day === null ? null : displayLongDate(day.day);
  if (title.length > 0 && written !== null) return w.prioritiesAtMeeting(title, written);
  return w.prioritiesByMotion;
}

// ── What the person sends (D3), and a paper sheet ────────────────────────────────────────────────

/** A form's state: each statement's answer, each group's comment, each open question's answer. */
export interface AnswerDraft {
  ratings: Readonly<Record<string, string>>;
  comments: Readonly<Record<string, string>>;
  openAnswers: Readonly<Record<string, string>>;
}

export const EMPTY_DRAFT: AnswerDraft = { ratings: {}, comments: {}, openAnswers: {} };

/** The answers already sent (D2's `my_answers`) as a form's starting state. */
export function draftFromMine(
  mine: BoardSelfAssessmentQuestionsForMeOut['my_answers'],
): AnswerDraft {
  if (mine === null || mine === undefined) return EMPTY_DRAFT;
  const ratings: Record<string, string> = {};
  for (const row of mine.answers ?? []) {
    if (typeof row.statement_id === 'string' && typeof row.answer === 'string') {
      ratings[row.statement_id] = row.answer;
    }
  }
  const comments: Record<string, string> = {};
  for (const row of mine.comments ?? []) {
    if (typeof row.group_id === 'string' && typeof row.text === 'string')
      comments[row.group_id] = row.text;
  }
  const openAnswers: Record<string, string> = {};
  for (const row of mine.open_answers ?? []) {
    if (typeof row.question_id === 'string' && typeof row.text === 'string') {
      openAnswers[row.question_id] = row.text;
    }
  }
  return { ratings, comments, openAnswers };
}

/** Whether a form holds anything to send: an answer, a comment or an open answer (§4.4). */
export function hasAnything(draft: AnswerDraft): boolean {
  return (
    Object.values(draft.ratings).some((value) => value.length > 0) ||
    Object.values(draft.comments).some((value) => value.trim().length > 0) ||
    Object.values(draft.openAnswers).some((value) => value.trim().length > 0)
  );
}

const ANSWERS = ['consistently', 'usually', 'sometimes', 'rarely_or_never', 'dont_know'] as const;
type Answer = (typeof ANSWERS)[number];

function isAnswer(value: string): value is Answer {
  return (ANSWERS as readonly string[]).includes(value);
}

/**
 * The body a form sends (`BoardSelfAssessmentAnswersIn`), in the questions' own order: only what is
 * asked, a blank comment or open answer left out (the backend drops one anyway).
 */
export function answersIn(
  draft: AnswerDraft,
  questions: {
    groups: readonly BoardSelfAssessmentGroupOut[];
    open_questions?: readonly BoardSelfAssessmentOpenQuestionOut[] | null;
  },
) {
  const answers: { statement_id: string; answer: Answer }[] = [];
  const comments: { group_id: string; text: string }[] = [];
  for (const group of questions.groups) {
    for (const statement of group.statements) {
      const answer = draft.ratings[statement.statement_id];
      if (answer !== undefined && isAnswer(answer)) {
        answers.push({ statement_id: statement.statement_id, answer });
      }
    }
    const comment = draft.comments[group.group_id];
    if (comment !== undefined && comment.trim().length > 0) {
      comments.push({ group_id: group.group_id, text: comment });
    }
  }
  const openAnswers: { question_id: string; text: string }[] = [];
  for (const question of questions.open_questions ?? []) {
    const text = draft.openAnswers[question.question_id];
    if (text !== undefined && text.trim().length > 0) {
      openAnswers.push({ question_id: question.question_id, text });
    }
  }
  return { answers, comments, open_answers: openAnswers };
}

// ── What a person's unsent answers keep in this tab (`director-drafts.ts`) ──────────────────────────

const FIELD_PREFIX = { ratings: 'r:', comments: 'c:', openAnswers: 'o:' } as const;

/**
 * The fields a draft keeps: only what the person chose or changed since their last send, never the
 * answers the server sent back (the drafts module's rule). A choice is kept as its answer's word; a
 * cleared comment as an empty string is no field at all.
 */
export function draftFields(draft: AnswerDraft, sent: AnswerDraft): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const part of ['ratings', 'comments', 'openAnswers'] as const) {
    for (const [id, value] of Object.entries(draft[part])) {
      if (value !== (sent[part][id] ?? '')) fields[`${FIELD_PREFIX[part]}${id}`] = value;
    }
  }
  return fields;
}

/** A kept draft laid back over the answers last sent: each kept field where it belongs. */
export function applyDraftFields(
  sent: AnswerDraft,
  kept: Readonly<Record<string, string>>,
): AnswerDraft {
  const next = {
    ratings: { ...sent.ratings },
    comments: { ...sent.comments },
    openAnswers: { ...sent.openAnswers },
  };
  for (const [key, value] of Object.entries(kept)) {
    for (const part of ['ratings', 'comments', 'openAnswers'] as const) {
      const prefix = FIELD_PREFIX[part];
      if (key.startsWith(prefix)) next[part][key.slice(prefix.length)] = value;
    }
  }
  return next;
}

// ── The person's row on "Your boards" (the attention kind `self_assessment`) ─────────────────────

export interface SelfAssessmentAwaiting {
  assessmentId: string;
  label: string;
  dueOn: string;
}

/**
 * The board's open self-assessment waiting on this person, as the attention read names it (a
 * recipient who hasn't answered in Board or on paper; withheld while the flag is off). Read field by
 * field: the generated union is a compile-time claim, and a malformed row is left out.
 */
export function selfAssessmentsAwaiting(items: unknown): SelfAssessmentAwaiting[] {
  return arrayOf<Record<string, unknown>>(items)
    .filter(
      (item) =>
        item.kind === 'self_assessment' &&
        typeof item.assessment_id === 'string' &&
        typeof item.label === 'string' &&
        typeof item.due_on === 'string',
    )
    .map((item) => ({
      assessmentId: item.assessment_id as string,
      label: item.label as string,
      dueOn: item.due_on as string,
    }));
}
